import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';

// Pages
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardLayout from './components/layout/DashboardLayout';
import TodayDashboard from './pages/TodayDashboard';
import CategoriesPage from './pages/CategoriesPage';
import ItemsPage from './pages/ItemsPage';
import TargetsPage from './pages/TargetsPage';
import OffersPage from './pages/OffersPage';
import PurchasesPage from './pages/PurchasesPage';
import ReportsPage from './pages/ReportsPage';
import PerformancePage from './pages/PerformancePage';
import UsersPage from './pages/UsersPage';
import RolesPage from './pages/RolesPage';
import SettingsPage from './pages/SettingsPage';
import AuditLogPage from './pages/AuditLogPage';
import AIAssistantPage from './pages/AIAssistantPage';
import ZohoInventoryPage from './pages/ZohoInventoryPage';
import BulkImportExportPage from './pages/BulkImportExportPage';
import AIEngineDashboard from './pages/AIEngineDashboard';
import PMSPage from './pages/PMSPage';
import MetricsPage from './pages/MetricsPage';
import DirectoryPage from './pages/DirectoryPage';
import ReconcilePage from './pages/ReconcilePage';
import RiskDashboardPage from './pages/RiskDashboardPage';
import CRMMatchingPage from './pages/CRMMatchingPage';
import AliasCatalogPage from './pages/AliasCatalogPage';
import BidEnginePage from './pages/BidEnginePage';
import OfferStorePage from './pages/OfferStorePage';
import TeamDashboardPage from './pages/TeamDashboardPage';
import VendorPaymentPage from './pages/VendorPaymentPage';
import PerformanceReviewsPage from './pages/PerformanceReviewsPage';
import ConductPage from './pages/ConductPage';
import MeetingsPage from './pages/MeetingsPage';
import SourcingPage from './pages/SourcingPage';
import LeadsPage from './pages/LeadsPage';
import { ModuleAccessGate } from './components/common/ModuleAccessGate';

import './App.css';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-2 text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
}

// 21 Oct 2026 (security follow-up, item 2): Offers & Purchases is retired — the
// historical read-only pages stay reachable by direct URL for a Super Admin only,
// everyone else is redirected home.
function SuperAdminRoute({ children }) {
  const { isSuperAdmin } = useAuth();
  if (!isSuperAdmin) {
    return <Navigate to="/" replace />;
  }
  return children;
}

// 21 Oct 2026 (security follow-up, item 9): price analytics (Risk Dashboard, AI Trade
// Engine) is Admin-level or assigned-Product-Manager only — everyone else redirected home.
// F4 Step 6 (1 Nov 2026): kept on hasRole('product_manager') deliberately — is_pm_role()
// also covers buyer_profile (by Step 4 Part 1 design, for bid-engine/targets scoping),
// which would newly grant every buyer this route. See OPEN_QUESTIONS_FOR_LEX.md.
function PriceAnalyticsRoute({ children }) {
  const { isAdmin, hasRole } = useAuth();
  const allowed = isAdmin || hasRole('product_manager');
  if (!allowed) {
    return <Navigate to="/" replace />;
  }
  return children;
}

// Vendor Payment Phase 3 / Performance Reviews: users without Dashboard access land on
// the first module they can open instead of the (inaccessible) Today Dashboard at "/".
function HomeRoute() {
  const { canAccess } = useAuth();
  if (canAccess('DASHBOARD', 'VIEW')) {
    return <TodayDashboard />;
  }
  if (canAccess('VENDOR_PAYMENT_APPROVAL', 'VIEW')) {
    return <Navigate to="/vendor-payment" replace />;
  }
  if (canAccess('PERFORMANCE_REVIEWS', 'VIEW')) {
    return <Navigate to="/reviews" replace />;
  }
  return <Navigate to="/login" replace />;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      {/* Not auth-gated either way: a user clicking an emailed reset link must be
          able to land here whether or not they still have a stale session open. */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Protected routes */}
      <Route path="/" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route index element={<HomeRoute />} />
        <Route path="categories" element={<CategoriesPage />} />
        <Route path="items" element={<ItemsPage />} />
        <Route path="targets" element={<TargetsPage />} />
        <Route path="offers" element={<SuperAdminRoute><OffersPage /></SuperAdminRoute>} />
        <Route path="purchases" element={<SuperAdminRoute><PurchasesPage /></SuperAdminRoute>} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="performance" element={<ModuleAccessGate moduleId="PERFORMANCE" pageName="Performance"><PerformancePage /></ModuleAccessGate>} />
        <Route path="ai-assistant" element={<AIAssistantPage />} />
        <Route path="ai-engine" element={<PriceAnalyticsRoute><AIEngineDashboard /></PriceAnalyticsRoute>} />
        <Route path="pms" element={<ModuleAccessGate moduleId="PMS" pageName="Performance (PMS)"><PMSPage /></ModuleAccessGate>} />
        <Route path="metrics" element={<MetricsPage />} />
        <Route path="directory" element={<DirectoryPage />} />
        <Route path="zoho-inventory" element={<ZohoInventoryPage />} />
        <Route path="bulk-import-export" element={<BulkImportExportPage />} />
        <Route path="users" element={<ModuleAccessGate moduleId="USERS" pageName="Users"><UsersPage /></ModuleAccessGate>} />
        <Route path="roles" element={<ModuleAccessGate moduleId="ROLES_PROFILES" pageName="Roles & Profiles"><RolesPage /></ModuleAccessGate>} />
        <Route path="settings" element={<ModuleAccessGate moduleId="SETTINGS" pageName="Settings"><SettingsPage /></ModuleAccessGate>} />
        <Route path="audit-log" element={<ModuleAccessGate moduleId="AUDIT_LOG" pageName="Audit Log"><AuditLogPage /></ModuleAccessGate>} />
        <Route path="reconcile" element={<ReconcilePage />} />
        <Route path="risk-dashboard" element={<PriceAnalyticsRoute><RiskDashboardPage /></PriceAnalyticsRoute>} />
        <Route path="crm-matching" element={<CRMMatchingPage />} />
        <Route path="alias-catalog" element={<AliasCatalogPage />} />
        <Route path="bid-engine" element={<BidEnginePage />} />
        <Route path="offer-store" element={<ModuleAccessGate moduleId="OFFER_STORE" pageName="Offer Store"><OfferStorePage /></ModuleAccessGate>} />
        <Route path="leads" element={<ModuleAccessGate moduleId="LEADS" pageName="Leads"><LeadsPage /></ModuleAccessGate>} />
        <Route path="my-team" element={<TeamDashboardPage />} />
        <Route path="vendor-payment" element={<VendorPaymentPage />} />
        <Route path="reviews" element={<PerformanceReviewsPage />} />
        <Route path="conduct" element={<ConductPage />} />
        <Route path="meetings" element={<MeetingsPage />} />
        <Route path="meetings/:meetingId" element={<MeetingsPage />} />
        <Route path="sourcing" element={<ModuleAccessGate moduleId="SOURCING" pageName="Sourcing"><SourcingPage /></ModuleAccessGate>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors closeButton />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
